import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import { DisclosureCard } from "../primitives/DisclosureCard";
import { Eyebrow } from "../primitives/Eyebrow";
import { RowAction } from "../primitives/RowAction";
import { isLocalBellRow, type BellItem } from "./bellRows";
import { displayParams, type InboxParamFormatter } from "./inboxParams";

// One row of the Home inbox — same family as the Library mini cards
// (LibraryItemCard/LibraryItemCardActions), NOT a new card component:
// a bordered box, a message that shows a PART and discloses the rest when
// it's long (DisclosureCard, the canonical "titled fold" — WeeklyTargetsCard
// / Progress's "More detail" fold), and a text-action row underneath
// (RowAction, the house Vedi ▾ · Segui · Modifica idiom). The actions sit
// OUTSIDE DisclosureCard's header/children on purpose: header is one single
// Pressable (tap anywhere in it toggles the fold), so a real action button
// living inside it would be a Pressable nested in a Pressable — DisclosureCard's
// own `boxStyle` is blanked here so ITS border doesn't double the outer
// card's, but the header/body padding it already owns is kept as-is.
const BODY_FOLD_THRESHOLD = 88;

// Splits `text` at a word boundary near `max` so the always-visible header
// preview and the disclosed body never show the same characters twice
// (INBOX-1: DisclosureCard's header stays on screen once opened, so a body
// that repeated the preview's own words read as duplicated text). The header
// keeps `head` (+ "…" when there's a `tail`); the body picks up exactly where
// the header's ellipsis left off, so together they read as one continuous
// text with nothing repeated and nothing missing.
function splitLong(text: string, max: number): { head: string; tail: string } {
  if (text.length <= max) return { head: text, tail: "" };
  const cut = text.lastIndexOf(" ", max);
  const boundary = cut > 0 ? cut : max;
  return {
    head: text.slice(0, boundary).trimEnd(),
    tail: text.slice(boundary).trimStart(),
  };
}

/** One button under the card: words, and the danger tone only beside them. */
export type InboxCardAction = {
  label: string;
  onPress: () => void;
  destructive?: boolean;
  /** What the action does, when its words alone do not say it. Spoken with
   *  the label: RNW drops accessibilityHint. */
  hint?: string;
  /** Shown instead of `label` while the row is busy: the words say the
   *  action is running, not only a dimmed button. */
  busyLabel?: string;
};

export type InboxCardProps = {
  theme: UiTheme;
  item: BellItem;
  /** True while THIS item's write is in flight: disables the row so a
   *  second tap can't double-fire. */
  busy: boolean;
  /** The row's own actions, in order; the caller picks them per origin. */
  actions: readonly InboxCardAction[];
  /** The host's param formatter (InboxProvider's `formatParams`). */
  formatParams?: InboxParamFormatter;
};

export function InboxCard({ theme, item, busy, actions, formatParams }: InboxCardProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  const params = useMemo(
    () => displayParams(item.params, t, formatParams),
    [item.params, t, formatParams],
  );

  const title = t(item.titleKey, params);
  const body = t(item.bodyKey, params);
  // INBOX-10: one list carries every role, so the card names the role it
  // is for, in words. Antenore: «è la notifica che deve dire se è coach,
  // admin o atleta». Same labels as the role switcher.
  // #294: a row the phone wrote names the phone instead, which is also why
  // the athlete's other devices do not show it.
  const role = isLocalBellRow(item)
    ? t("inbox.local.eyebrow")
    : t(`experience.${item.audience}`);

  // The inviter's free-text note (params.note — coachEdge proposals only,
  // apps/api/src/routes/inbox.ts's derivePendingEdgeItems) is the ONE clue
  // this row can honestly offer about who is on the other end (the
  // recipient's own profile read is gated on an ACTIVE edge, which a
  // pending invite isn't — see that route's comment). It is someone ELSE's
  // text, so it renders quoted, truncated, verbatim: no markup, no
  // interpretation — same treatment CoachEdgesSection.tsx already gives
  // `edge.inviteNote`.
  const note = typeof item.params?.note === "string" ? item.params.note : null;
  // Either piece running long earns the fold — the note is the only thing
  // a pending-invite row says about who is on the other end (the sender's
  // name isn't available, see above), so it gets the SAME treatment as
  // body, not a silent numberOfLines clamp with no way to open it: a
  // sighted reader saw only 3 lines while a screen reader (which ignores
  // numberOfLines) always heard it whole — the exact split this fixes.
  // Each piece is split independently: a long note with a short body (or
  // vice versa) only earns a tail — and so a repeated header line — for
  // the piece that's actually long.
  const bodySplit = useMemo(
    () => splitLong(body, BODY_FOLD_THRESHOLD),
    [body],
  );
  const noteSplit = useMemo(
    () => (note !== null ? splitLong(note, BODY_FOLD_THRESHOLD) : null),
    [note],
  );
  const isLong = bodySplit.tail !== "" || (noteSplit !== null && noteSplit.tail !== "");

  const bodyPreview = bodySplit.tail ? `${bodySplit.head}…` : bodySplit.head;
  const notePreview = noteSplit
    ? noteSplit.tail
      ? `"${noteSplit.head}…`
      : `"${noteSplit.head}"`
    : null;

  const header = (
    <View style={styles.headText}>
      <Eyebrow theme={theme}>{role}</Eyebrow>
      <Text style={styles.title} numberOfLines={2}>
        {title}
      </Text>
      <Text style={styles.body} numberOfLines={bodySplit.tail ? 2 : undefined}>
        {bodyPreview}
      </Text>
      {notePreview ? (
        <Text
          style={styles.note}
          numberOfLines={noteSplit?.tail ? 2 : undefined}
        >
          {notePreview}
        </Text>
      ) : null}
    </View>
  );

  const spoken = (label: string) => `${label} — ${title}`;

  return (
    <View style={styles.card}>
      <DisclosureCard
        theme={theme}
        header={header}
        disabled={!isLong}
        // Full title + body + note, not the visually-truncated preview: a
        // collapsed row is otherwise silent (DisclosureCard's own doc), so
        // this is the only place a screen reader hears the whole message.
        accessibilityLabel={`${role}. ${title}. ${body}${note ? `. "${note}"` : ""}`}
        boxStyle={styles.disclosureBox}
        bodyStyle={styles.disclosureBody}
      >
        {/* Only the leftover tail — whatever the header preview above
            already shows stays there, not repeated here (INBOX-1). */}
        {bodySplit.tail ? <Text style={styles.body}>{bodySplit.tail}</Text> : null}
        {noteSplit?.tail ? (
          <Text style={styles.note}>{noteSplit.tail}"</Text>
        ) : null}
      </DisclosureCard>
      <View style={styles.actions}>
        {actions.map((a) => {
          const label = busy && a.busyLabel ? a.busyLabel : a.label;
          return (
            <RowAction
              key={a.label}
              theme={theme}
              label={label}
              onPress={a.onPress}
              disabled={busy}
              destructive={a.destructive}
              accessibilityLabel={
                a.hint ? `${spoken(label)}. ${a.hint}` : spoken(label)
              }
            />
          );
        })}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, radius: r, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    card: {
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
    },
    // DisclosureCard's own box (border + background) is blanked: this
    // card's outer `card` box above is the ONE visible border — a second
    // one here would be the card-in-card nesting HomeView's own doc bans.
    disclosureBox: { borderWidth: 0, backgroundColor: "transparent" },
    // gap, not just paddingTop: the fold can carry both the full body AND
    // the full note (isLong now considers either one), so the two need
    // their own rhythm between them, same as headText's collapsed rhythm.
    disclosureBody: { paddingTop: s.s1, gap: s.s1 },
    headText: { flex: 1, gap: s.s1 },
    title: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    body: { color: p.ink2, fontSize: t.sizes.sm },
    // Quoted free text from the OTHER user — same voice CoachEdgesSection.tsx
    // already gives `edge.inviteNote` (ink3, xs, italic): visually distinct
    // from this app's own copy above it, on purpose. No padding of its own:
    // it now lives inside headText/disclosureBody, both of which already
    // space their children via `gap`.
    note: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontStyle: "italic",
    },
    // Same rhythm as LibraryItemCard's actions row: wraps, and both gaps
    // clear 2×hitSlop so adjacent buttons' touch zones never overlap.
    actions: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: s.s5,
      rowGap: s.s5,
      paddingHorizontal: s.s4,
      paddingBottom: s.s4,
    },
  });
}
